import type { Scale } from '@tunasong/schemas'
import type { Chord } from '../chord/index.js'
import { isChordNotes, isChordVariant } from '../chord/chord-types.js'
import { getNotes as getChordNotes } from '../chord/get-notes.js'
import { getNoteChromaticDistance, getNoteChromaticIndex } from '../note/note-lib.js'
import { ScaleLib } from '../scale/index.js'
import { getDiatonicFunction } from './diatonic.js'
import type { ChordBorrowing, ChordFunction } from './types.js'

/** Parallel modes, beyond the parallel major and minor, that a chord can be borrowed from. */
export type ModalBorrowingSource = 'mixolydian' | 'dorian'

export interface BorrowedFunction {
  chordFunction: ChordFunction
  borrowing: ChordBorrowing
  /**
   * Set when the chord is only in the parallel Mixolydian or Dorian (not the
   * parallel major or minor), so the description can name that mode.
   */
  modalSource?: ModalBorrowingSource
}

/**
 * The characteristic chord each parallel mode lends (Bowie, GH #2147): the
 * Mixolydian ♭VII (B♭maj7 in C) and the Dorian major IV (F7 in C, A7 in E
 * minor). Other chords of these modes are left alone: the Mixolydian I7 is the
 * secondary dominant V/IV, and the rarer diminished chords stay chromatic.
 *
 * Mixolydian lends to major keys only. Its ♭VII is already diatonic in minor,
 * and Dmaj7 in E minor (C♯) stays chromatic (Bowie, GH #2136).
 */
const MODAL_BORROWINGS: { mode: ModalBorrowingSource; chordFunction: ChordFunction; keys: Scale['type'][] }[] = [
  { mode: 'mixolydian', chordFunction: '♭VII', keys: ['major'] },
  { mode: 'dorian', chordFunction: 'IV', keys: ['major', 'minor'] },
]

/**
 * If `chord` is a chord borrowed from the parallel mode of `scale`, return its
 * label and borrowing source. Otherwise return null.
 *
 * Strategy: build the parallel-mode scale (same root, opposite quality) and
 * check whether the chord is diatonic there. If so, re-use the diatonic label
 * the parallel scale produces (e.g., Eb in C major is the III of C minor →
 * labelled '♭III').
 *
 * Special case: ♭II (Phrygian / Neapolitan color) is not in the parallel
 * minor's natural diatonic set, so detect it explicitly: chord root is a
 * half-step above the tonic and chord is a plain major triad.
 */
export const getBorrowedFunction = (chord: Chord, scale: Scale): BorrowedFunction | null => {
  // Skip if the chord is already diatonic — that's not borrowing.
  if (getDiatonicFunction(chord, scale)) {
    return null
  }

  // Build the parallel-mode scale.
  const parallelType = scale.type === 'major' ? 'minor' : 'major'
  const parallelScale: Scale = { ...scale, type: parallelType }

  const parallelDiatonic = getDiatonicFunction(chord, parallelScale)
  if (parallelDiatonic) {
    return { chordFunction: parallelDiatonic, borrowing: 'parallel-mode' }
  }

  // Then the parallel Mixolydian and Dorian, in that order. The parallel minor
  // goes first, so B♭7 in C major stays the Aeolian backdoor dominant.
  for (const { mode, chordFunction, keys } of MODAL_BORROWINGS) {
    if (keys.includes(scale.type) && getDiatonicFunction(chord, { ...scale, type: mode }) === chordFunction) {
      // The diatonic check ignores alterations, so F7♭9 in C still lands on
      // the Dorian IV. Name Dorian only when every tone is in it (Bowie, GH
      // #2147); otherwise the chord reads as the dominant-quality IV.
      if (mode === 'dorian' && !isInParallelMode(chord, scale, 'dorian')) {
        return { chordFunction, borrowing: 'parallel-mode' }
      }
      return { chordFunction, borrowing: 'parallel-mode', modalSource: mode }
    }
  }

  // Phrygian / Neapolitan: ♭II in major and in minor, where it is the flamenco cadence chord (Bowie, GH #2369).
  if ((scale.type === 'major' || scale.type === 'minor') && isPhrygianII(chord, scale)) {
    return { chordFunction: '♭II', borrowing: 'parallel-mode' }
  }

  return null
}

/**
 * Is `chord` the Phrygian ♭II of `scale`? — chord is a plain major triad whose
 * root is a half-step above the scale's tonic.
 *
 * Plain-major-triad in this codebase is represented as `variant: ''` (see
 * lib/music-lib/src/chord/chord-types.ts — the empty string is the canonical
 * major triad; 'M' is an alias produced by the parser in some paths). We
 * accept either, but reject anything that adds extensions, minor, dim, etc.
 *
 * `getInterval` from scale.ts only returns a degree for notes that are in the
 * scale, so it can't tell us the distance from C to Db (Db is outside C
 * major). We use `getNoteChromaticDistance` instead — it's defined as
 * (to - from + 12) % 12 and gives a clean 0–11 semitone count.
 */
const isPhrygianII = (chord: Chord, scale: Scale): boolean => {
  if (isChordNotes(chord)) {
    return false
  }
  if (chord.variant !== '' && chord.variant !== 'M') {
    return false
  }
  return getNoteChromaticDistance(scale.root, chord.root) === 1
}

/**
 * True when every tone of `chord` is in the parallel `mode` of `scale` (same
 * root). Chords whose notes cannot be resolved return false, so an unknown
 * chord is never credited to a mode.
 *
 * Known gap: `getNotes` returns no notes for `7#11` and `7b13` (F7♯11 and
 * F7♭13 come back empty), while `7b9` / `7#9` include the altered tone. Those
 * two variants therefore always read "not in the mode". For the Dorian IV that
 * happens to be right (B♮ and D♭ are not in C Dorian), but it is the gap, not
 * the check, that decides it.
 */
export const isInParallelMode = (chord: Chord, scale: Scale, mode: NonNullable<Scale['type']>): boolean => {
  if (!isChordVariant(chord)) {
    return false
  }
  const chordNotes = getChordNotes(chord)
  if (chordNotes.length === 0) {
    return false
  }
  const modeIndices = new Set(ScaleLib.getNotesChromaticIndices({ ...scale, type: mode }))
  return chordNotes.every(note => modeIndices.has(getNoteChromaticIndex(note)))
}

/**
 * True when every tone of `chord` is in the parallel Mixolydian of `scale`.
 * Names the source mode of a borrowed ♭VII: the B♭ triad and B♭maj7 in C
 * major fit C Mixolydian, but B♭7's A♭ comes from the parallel minor
 * (Aeolian), where it is the backdoor dominant.
 */
export const isInParallelMixolydian = (chord: Chord, scale: Scale): boolean =>
  isInParallelMode(chord, scale, 'mixolydian')
